'use client';

import cx from 'classnames';
import {
  type CSSProperties,
  createContext,
  type Dispatch,
  type FC,
  type ReactNode,
  type RefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Button} from '../atoms/Button';

type DropdownContextType = {
  toggle: Dispatch<SetStateAction<boolean>>;
} | null;

export const DropdownContext = createContext<DropdownContextType>(null);

const useDropdown = (ref: RefObject<HTMLDivElement | null>, onClickOut: () => void) => {
  useEffect(() => {
    const clickOut = ({target}: MouseEvent) => {
      if (!ref?.current?.contains(target as Node)) onClickOut();
    };

    document.addEventListener('mousedown', clickOut);
    return () => document.removeEventListener('mousedown', clickOut);
  }, [ref, onClickOut]);

  return [onClickOut];
};

interface DropdownProps {
  open?: boolean;
  children?: ReactNode[];
  trigger?: ReactNode;
  style?: CSSProperties;
  content?: ReactNode;
  withPadding?: boolean;
  buttonVariant?: ButtonVariant;
  buttonSize?: ButtonSize;
  buttonClassName?: string;
  dropdownClassName?: string;
  offsetLeft?: boolean;
  setOpen?: (value: boolean) => void;
  disabled?: boolean;
  buttonQaIdModifier?: string;
  'data-qa-id'?: string;
  'data-qa-value'?: string;
}

// Please note: the Dropdown component already wraps the
// trigger in a Button, do not pass a Button in the trigger!
export const Dropdown: FC<DropdownProps> = ({
  open: isOpen,
  children,
  trigger,
  style,
  content,
  withPadding = false,
  buttonVariant = 'transparent',
  buttonSize,
  buttonClassName,
  dropdownClassName,
  offsetLeft = true,
  setOpen,
  disabled = false,
  buttonQaIdModifier = '',
  'data-qa-id': dataQaId = 'dropdown',
  'data-qa-value': dataQaValue,
}) => {
  const [open, toggle] = useState(isOpen || false);
  const ref = useRef<HTMLDivElement>(null);

  const toggleDropdown = useCallback(
    (state: boolean) => {
      toggle(state);
      if (setOpen) {
        setOpen(state);
      }
    },
    [setOpen]
  );
  const toggleContext = useMemo(() => ({toggle}), []);

  useDropdown(ref, () => toggleDropdown(false));

  useEffect(() => {
    if (isOpen !== undefined) toggle(isOpen);
  }, [isOpen]);

  return (
    <DropdownContext.Provider value={toggleContext}>
      <div
        ref={ref}
        style={{position: 'relative', ...style}}
        data-qa-id={dataQaId}
        data-qa-value={dataQaValue}
        className="dropdown__container"
      >
        {/* @ts-expect-error: Button has not been converted to TypeScript yet */}
        <Button
          onClick={() => toggleDropdown(!open)}
          disabled={disabled}
          data-qa-id={`dropdown__trigger${buttonQaIdModifier ? `--${buttonQaIdModifier}` : ''}`}
          variant={buttonVariant}
          size={buttonSize}
          className={buttonClassName}
        >
          {children?.[0] || trigger}
        </Button>
        {open && (children?.[1] || content) && (
          <div
            className={cx(
              'dropdown__menu',
              {
                'dropdown__menu--padding': withPadding,
                'dropdown__menu--offset-left': offsetLeft,
              },
              dropdownClassName
            )}
          >
            {children?.[1] || content}
          </div>
        )}
      </div>
    </DropdownContext.Provider>
  );
};
